const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

export interface EmailContact {
  id: string;
  ownerKey: string;
  nombre: string;
  email: string;
  notas: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailContactInput {
  nombre: string;
  email: string;
  notas?: string | null;
}

interface ErrorResponse {
  error?: string;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ErrorResponse;
    return body.error ?? 'contacts_request_failed';
  } catch {
    return 'contacts_request_failed';
  }
}

async function requestJson<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function listEmailContacts(token: string, query?: string): Promise<EmailContact[]> {
  const params = query?.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
  const data = await requestJson<{ contacts: EmailContact[] }>(token, `/contacts${params}`);
  return data.contacts;
}

export function createEmailContact(token: string, input: EmailContactInput): Promise<EmailContact> {
  return requestJson<EmailContact>(token, '/contacts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateEmailContact(
  token: string,
  id: string,
  input: EmailContactInput
): Promise<EmailContact> {
  return requestJson<EmailContact>(token, `/contacts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function deleteEmailContact(token: string, id: string): Promise<void> {
  return requestJson<void>(token, `/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Alta o actualización por email; marca last_used_at. */
export function rememberEmailContact(token: string, input: EmailContactInput): Promise<EmailContact> {
  return requestJson<EmailContact>(token, '/contacts/remember', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type ClinicTeamRecipientRole = 'owner' | 'assistant';

export interface ClinicTeamRecipient {
  role: ClinicTeamRecipientRole;
  email: string;
  nombre: string;
  assistantId?: string;
}

/** Dueño + asistentes con acceso a la clínica (para preselección al enviar). */
export async function listClinicTeamRecipients(
  token: string,
  clinicaId?: string
): Promise<ClinicTeamRecipient[]> {
  const params = clinicaId?.trim() ? `?clinicaId=${encodeURIComponent(clinicaId.trim())}` : '';
  const data = await requestJson<{ recipients: ClinicTeamRecipient[] }>(token, `/contacts/team${params}`);
  return data.recipients;
}
